/**
 * ManualQuoteEntryDialog
 * ----------------------
 * Admin-side dialog to log OR EDIT a supplier quote. Two modes:
 *
 *   • CREATE mode (existingQuote == null) → POST /admin/backorders/{id}/supplier-quotes/manual
 *     For quotes that came in by phone / email / fax.
 *
 *   • EDIT mode (existingQuote != null) → PATCH /admin/supplier-quotes/{quote_id}
 *     Works for BOTH portal-submitted and admin-manual quotes.
 *
 * Supplier matching (create mode only):
 *   • If admin picks a supplier from the dropdown → supplier_id sent directly
 *   • Otherwise the backend matches by email and falls back to anonymous
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { X, Save, Loader2, Plus, Trash2, Phone, Mail, Building2, Pencil, Maximize2, Minimize2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const RECEIVED_VIA = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "fax", label: "Fax" },
  { value: "in-person", label: "In person" },
  { value: "other", label: "Other" },
];

export default function ManualQuoteEntryDialog({ backorder, token, onClose, onSaved, existingQuote = null }) {
  const isEdit = !!existingQuote;
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  // In EDIT mode the supplier is already locked in — start in "new" (free-text)
  // mode pre-filled with the quote's supplier fields so admin can correct typos
  // without overwriting the original supplier_id link.
  const [supplierMode, setSupplierMode] = useState(isEdit ? "new" : "existing");
  const [supplierId, setSupplierId] = useState(existingQuote?.supplier_id || "");
  const [newSupplier, setNewSupplier] = useState({
    name: existingQuote?.supplier_name || "",
    email: existingQuote?.supplier_email || "",
    phone: existingQuote?.supplier_phone || "",
    contact: existingQuote?.supplier_contact || "",
  });

  // Detect M&R backorders — when present we display labor/travel-aware
  // columns and compute extensions differently (hours×rate, miles×rate, flat).
  const isMR = backorder?.order_type === "maintenance_repair";

  // Line items: in EDIT mode pre-fill from quote.item_quotes; in CREATE mode
  // default to the backorder's items with empty price/qty fields.
  const initialItems = useMemo(() => {
    if (isEdit && Array.isArray(existingQuote?.item_quotes) && existingQuote.item_quotes.length > 0) {
      return existingQuote.item_quotes.map((it) => ({
        name: it.name || "",
        item_type: it.item_type || "part",
        quantity_requested: it.quantity_requested ?? "",
        qty_available: it.qty_available ?? "",
        unit_price: it.unit_price ?? "",
        lead_time: it.lead_time || "",
        // M&R extras
        hours: it.hours ?? "",
        hourly_rate: it.hourly_rate ?? "",
        miles: it.miles ?? "",
        per_mile_rate: it.per_mile_rate ?? "",
        flat_fee: it.flat_fee ?? "",
      }));
    }
    return (backorder.items || []).map((it) => {
      const itype = it.item_type || "part";
      // For M&R labor/travel, pre-populate the "requested" quantity field
      // with the relevant unit (hours / miles) so the supplier quote table
      // makes intuitive sense. The qty × price extension auto-computes the
      // line total correctly in all three cases.
      let qty_req = it.quantity || 1;
      let unit_hint = "";
      if (itype === "labor") {
        qty_req = it.hours || it.quantity || 1;
        unit_hint = "hours";
      } else if (itype === "travel") {
        qty_req = it.miles || (it.flat_fee ? 1 : (it.quantity || 1));
        unit_hint = it.miles ? "miles" : "trip";
      }
      return {
        name: it.name || "",
        item_type: itype,
        unit_hint,
        quantity_requested: qty_req,
        qty_available: "",
        unit_price: "",
        lead_time: "",
        hours: it.hours ?? "",
        hourly_rate: it.hourly_rate ?? "",
        miles: it.miles ?? "",
        per_mile_rate: it.per_mile_rate ?? "",
        flat_fee: it.flat_fee ?? "",
      };
    });
  }, [backorder.items, existingQuote, isEdit]);

  const [items, setItems] = useState(initialItems);
  const [totalEstimate, setTotalEstimate] = useState(
    existingQuote?.total_estimate != null ? String(existingQuote.total_estimate) : ""
  );
  // Tracks whether the admin manually typed into the Total field. Once they
  // do, we stop clobbering their value on every line-item edit. Clearing the
  // field resets the flag so auto-compute resumes.
  const [totalUserOverrode, setTotalUserOverrode] = useState(
    existingQuote?.total_estimate != null
  );
  const [leadTime, setLeadTime] = useState(existingQuote?.lead_time || "");
  const [freightEstimate, setFreightEstimate] = useState(
    existingQuote?.freight_estimate != null ? String(existingQuote.freight_estimate) : ""
  );
  const [notes, setNotes] = useState(existingQuote?.notes || "");
  const [receivedVia, setReceivedVia] = useState(existingQuote?.received_via || "email");
  const [receivedAt, setReceivedAt] = useState(() => {
    if (existingQuote?.received_at) {
      // Convert ISO to datetime-local format (YYYY-MM-DDTHH:MM)
      try { return new Date(existingQuote.received_at).toISOString().slice(0, 16); }
      catch { return new Date().toISOString().slice(0, 16); }
    }
    return new Date().toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);
  // Default = half-page (max-w-4xl). Expanded = nearly full screen so admin
  // can see all 5 line-item columns + totals row at once without scrolling.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Only fetch the supplier list when we might need the dropdown (create mode).
    if (isEdit) { setLoadingSuppliers(false); return; }
    axios.get(`${API}/api/suppliers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSuppliers(r.data?.suppliers || r.data || []))
      .catch(() => setSuppliers([]))
      .finally(() => setLoadingSuppliers(false));
  }, [token, isEdit]);

  // Auto-compute total whenever any line item changes — both in CREATE and
  // EDIT mode. Skipped if the admin has manually overridden the total field
  // (so we don't clobber a hand-keyed number). Works for all order types —
  // M&R labor/travel use `qty_available × unit_price` semantics too
  // (hours × $/hr, miles × $/mi, or 1 × flat-fee).
  useEffect(() => {
    if (totalUserOverrode) return;
    const computed = items.reduce((sum, it) => {
      const q = parseFloat(it.qty_available);
      const p = parseFloat(it.unit_price);
      if (!Number.isNaN(q) && !Number.isNaN(p)) sum += q * p;
      return sum;
    }, 0);
    setTotalEstimate(computed > 0 ? computed.toFixed(2) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const setItem = (idx, field, val) =>
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  const addItem = () => setItems((arr) => [...arr, { name: "", item_type: "part", quantity_requested: 1, qty_available: "", unit_price: "", lead_time: "", hours: "", hourly_rate: "", miles: "", per_mile_rate: "", flat_fee: "" }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const selectedSupplier = suppliers.find((s) => s.id === supplierId);

  const submit = async (e) => {
    // Guard against the Enter-key bubbling up to a parent form (which was
    // closing the dialog) AND default form submit page reload.
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    // Validation
    const supplierName = supplierMode === "existing"
      ? (selectedSupplier?.name || selectedSupplier?.company_name || "")
      : newSupplier.name.trim();
    if (!supplierName) {
      toast.error("Pick a supplier or enter a new supplier name");
      return;
    }
    setSaving(true);
    try {
      const cleanItems = items
        .filter((it) => it.name?.trim() || it.qty_available || it.unit_price)
        .map((it) => ({
          name: it.name?.trim() || "",
          item_type: it.item_type || "part",
          unit_hint: it.unit_hint || null,
          quantity_requested: parseInt(it.quantity_requested) || null,
          qty_available: it.qty_available === "" ? null : parseFloat(it.qty_available),
          unit_price: it.unit_price === "" ? null : parseFloat(it.unit_price),
          lead_time: it.lead_time?.trim() || null,
        }));

      if (isEdit) {
        // PATCH: only send the editable fields. Preserve original supplier_id link
        // (backend ignores it on PATCH). supplier_email empty-string -> null so
        // pydantic EmailStr validation doesn't reject the payload.
        const cleanEmail = newSupplier.email.trim().toLowerCase();
        const patchPayload = {
          supplier_name: supplierName,
          supplier_email: cleanEmail || null,
          supplier_phone: newSupplier.phone.trim() || null,
          supplier_contact: newSupplier.contact.trim() || null,
          item_quotes: cleanItems,
          total_estimate: totalEstimate === "" ? null : parseFloat(totalEstimate),
          lead_time: leadTime.trim() || null,
          freight_estimate: freightEstimate === "" ? null : parseFloat(freightEstimate),
          notes: notes.trim() || null,
          received_via: receivedVia,
          received_at: receivedAt ? new Date(receivedAt).toISOString() : null,
        };
        await axios.patch(
          `${API}/api/admin/supplier-quotes/${existingQuote.id}`,
          patchPayload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success(`Quote from ${supplierName} updated`);
      } else {
        const payload = {
          supplier_id: supplierMode === "existing" ? (supplierId || null) : null,
          supplier_name: supplierName,
          supplier_email: supplierMode === "existing"
            ? (selectedSupplier?.email || null)
            : (newSupplier.email.trim().toLowerCase() || null),
          supplier_phone: supplierMode === "existing"
            ? (selectedSupplier?.phone || null)
            : (newSupplier.phone.trim() || null),
          supplier_contact: supplierMode === "existing"
            ? (selectedSupplier?.contact || null)
            : (newSupplier.contact.trim() || null),
          item_quotes: cleanItems,
          total_estimate: totalEstimate === "" ? null : parseFloat(totalEstimate),
          lead_time: leadTime.trim() || null,
          freight_estimate: freightEstimate === "" ? null : parseFloat(freightEstimate),
          notes: notes.trim() || null,
          received_via: receivedVia,
          received_at: receivedAt ? new Date(receivedAt).toISOString() : null,
        };
        await axios.post(
          `${API}/api/admin/backorders/${backorder.id}/supplier-quotes/manual`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success(`Quote from ${supplierName} logged`);
      }
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join("; ")
        : "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="manual-quote-dialog"
      onClick={(e) => e.stopPropagation()}
    >
      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          // Enter inside a Textarea = newline (let it through). Anywhere else
          // we explicitly drive submit ourselves to avoid (a) browser native
          // form-submit being swallowed by ancestor handlers, and (b) Enter
          // accidentally triggering the drawer/backdrop close.
          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
            e.preventDefault();
            e.stopPropagation();
            if (!saving) submit(e);
          }
          // Swallow Escape so the dialog can't be unmounted by a parent
          // listener mid-edit; users still have the X / Cancel buttons.
          if (e.key === "Escape") {
            e.stopPropagation();
          }
        }}
        className={`bg-white rounded-lg w-full shadow-2xl flex flex-col transition-all duration-200 ${
          expanded
            ? "max-w-[96vw] h-[95vh] my-4"
            : "max-w-4xl my-8 max-h-[92vh]"
        }`}
      >
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              {isEdit ? (
                <Pencil className="w-5 h-5 text-amber-600" />
              ) : (
                <Plus className="w-5 h-5 text-emerald-600" />
              )}
              {isEdit ? `Edit Quote · ${existingQuote.supplier_name || ""}` : "Enter Quote Manually"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEdit
                ? "Update any field — supplier name, totals, lead time, line items, notes. Audit-logged."
                : "For quotes received by email, phone, fax, etc. Saved as a normal `supplier_quote`."}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100 transition-colors"
              title={expanded ? "Collapse to half-page" : "Expand to full page"}
              data-testid="mqe-expand-toggle"
            >
              {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100" title="Close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          {/* Supplier picker */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Supplier</Label>
              {!isEdit && (
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setSupplierMode("existing")}
                    className={`px-2 py-1 rounded ${supplierMode === "existing" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}
                    data-testid="mqe-mode-existing"
                  >From dropdown</button>
                  <button
                    type="button"
                    onClick={() => setSupplierMode("new")}
                    className={`px-2 py-1 rounded ${supplierMode === "new" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}
                    data-testid="mqe-mode-new"
                  >Enter manually</button>
                </div>
              )}
            </div>
            {supplierMode === "existing" ? (
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="mqe-supplier-select">
                  <SelectValue placeholder={loadingSuppliers ? "Loading suppliers…" : "Pick supplier from your list"} />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name || s.company_name}{s.email ? ` · ${s.email}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Supplier name *" value={newSupplier.name} onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })} data-testid="mqe-new-name" />
                <Input placeholder="Contact person (optional)" value={newSupplier.contact} onChange={(e) => setNewSupplier({ ...newSupplier, contact: e.target.value })} />
                <Input type="email" placeholder="supplier@email.com" value={newSupplier.email} onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })} data-testid="mqe-new-email" />
                <Input placeholder="Phone" value={newSupplier.phone} onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })} />
              </div>
            )}
            {supplierMode === "existing" && selectedSupplier && (
              <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700 flex items-center gap-3 flex-wrap">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-semibold">{selectedSupplier.name || selectedSupplier.company_name}</span>
                {selectedSupplier.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{selectedSupplier.email}</span>}
                {selectedSupplier.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{selectedSupplier.phone}</span>}
              </div>
            )}
          </section>

          {/* How received */}
          <section>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">How was this quote received?</Label>
            <p className="text-[11px] text-slate-500 mt-0.5 mb-2">Audit trail — so we know whether the supplier replied by email, phoned it in, faxed, etc.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Received via</Label>
                <Select value={receivedVia} onValueChange={setReceivedVia}>
                  <SelectTrigger className="h-9 mt-1" data-testid="mqe-received-via"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECEIVED_VIA.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Date / time received</Label>
                <Input type="datetime-local" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} className="h-9 mt-1" />
              </div>
            </div>
          </section>

          {/* Line items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Line items</Label>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  One row per SKU the supplier quoted. Fill <strong>Qty Avail</strong> and <strong>Unit Price</strong> — the Total below will auto-calculate.
                </p>
              </div>
              <button type="button" onClick={addItem} className="text-xs font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add line
              </button>
            </div>
            <div className="hidden min-[900px]:block overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left">Item / Description</th>
                    <th className="px-2 py-2 text-center w-24" title="Quantity originally requested on the backorder">
                      Qty Requested
                    </th>
                    <th className="px-2 py-2 text-center w-24" title="Quantity the supplier can actually fulfill on this quote">
                      Qty Available
                    </th>
                    <th className="px-2 py-2 text-right w-28" title="Supplier's unit price for this SKU (USD)">
                      Unit Price&nbsp;($)
                    </th>
                    <th className="px-2 py-2 text-left w-28" title="Lead time / ETA for this SKU (e.g. 5-7 days)">
                      Lead / ETA
                    </th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((it, i) => {
                    const itype = it.item_type || "part";
                    const showTypeBadge = isMR && itype !== "part";
                    const qtyLabel = itype === "labor" ? "hrs" : itype === "travel" && it.unit_hint === "miles" ? "mi" : itype === "travel" ? "trips" : "";
                    const priceLabel = itype === "labor" ? "$/hr" : itype === "travel" && it.unit_hint === "miles" ? "$/mi" : itype === "travel" ? "$/trip" : "";
                    return (
                    <tr key={i} className="hover:bg-slate-50/60" data-testid={`mqe-line-${i}`}>
                      <td className="px-2 py-1.5">
                        {showTypeBadge && (
                          <div className="mb-1">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${itype === "labor" ? "bg-indigo-50 text-indigo-700 border border-indigo-200" : "bg-sky-50 text-sky-700 border border-sky-200"}`}>
                              {itype}
                            </span>
                          </div>
                        )}
                        <Input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder={itype === "labor" ? "e.g. Labor: bearing replacement" : itype === "travel" ? "e.g. Site visit / mileage" : "e.g. Neuro Sponge 1/2&quot; x 3&quot;"} className="h-9 text-sm" />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" min="0" value={it.quantity_requested} onChange={(e) => setItem(i, "quantity_requested", e.target.value)} className="h-9 text-sm text-center" />
                        {showTypeBadge && qtyLabel && <div className="text-[9px] text-slate-400 text-center mt-0.5">{qtyLabel}</div>}
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" min="0" value={it.qty_available} onChange={(e) => setItem(i, "qty_available", e.target.value)} placeholder="0" className="h-9 text-sm text-center" data-testid={`mqe-qty-${i}`} />
                        {showTypeBadge && qtyLabel && <div className="text-[9px] text-slate-400 text-center mt-0.5">{qtyLabel}</div>}
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} placeholder="0.00" className="h-9 text-sm text-right" data-testid={`mqe-unit-${i}`} />
                        {showTypeBadge && priceLabel && <div className="text-[9px] text-slate-400 text-right mt-0.5">{priceLabel}</div>}
                      </td>
                      <td className="px-2 py-1.5">
                        <Input value={it.lead_time} onChange={(e) => setItem(i, "lead_time", e.target.value)} placeholder="e.g. 5-7 days" className="h-9 text-sm" />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button type="button" onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-600 p-1" title="Remove this line">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE — Stacked card view (< 900px). Same handlers as the table above. */}
            <div className="block min-[900px]:hidden space-y-3">
              {items.map((it, i) => (
                <div key={i} className="rounded-lg border border-slate-200 bg-white shadow-sm p-3 space-y-2.5" data-testid={`mqe-line-mobile-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Line&nbsp;{i + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      className="text-slate-400 hover:text-red-600 p-1 -mr-1"
                      title="Remove this line"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Item / Description</Label>
                    <Input
                      value={it.name}
                      onChange={(e) => setItem(i, "name", e.target.value)}
                      placeholder="e.g. Neuro Sponge 1/2&quot; x 3&quot;"
                      className="h-9 mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Qty Requested</Label>
                      <Input
                        type="number" min="0"
                        value={it.quantity_requested}
                        onChange={(e) => setItem(i, "quantity_requested", e.target.value)}
                        className="h-9 mt-1 text-center"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Qty Available</Label>
                      <Input
                        type="number" min="0"
                        value={it.qty_available}
                        onChange={(e) => setItem(i, "qty_available", e.target.value)}
                        placeholder="0"
                        className="h-9 mt-1 text-center"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Unit Price&nbsp;($)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={it.unit_price}
                        onChange={(e) => setItem(i, "unit_price", e.target.value)}
                        placeholder="0.00"
                        className="h-9 mt-1 text-right"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Lead / ETA</Label>
                      <Input
                        value={it.lead_time}
                        onChange={(e) => setItem(i, "lead_time", e.target.value)}
                        placeholder="e.g. 5-7 days"
                        className="h-9 mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Totals + lead + freight */}
          <section>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Quote totals</Label>
            <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
              Total auto-calculates from the line items above. Lead Time + Freight are the supplier&rsquo;s overall numbers for this quote (separate from per-line ETAs).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center justify-between">
                  <span>Total Estimate&nbsp;($)</span>
                  {totalUserOverrode && (
                    <button
                      type="button"
                      onClick={() => { setTotalUserOverrode(false); setTotalEstimate(""); }}
                      className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal"
                      data-testid="mqe-total-reset"
                      title="Resume auto-calculation from line items"
                    >
                      ↻ Auto
                    </button>
                  )}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={totalEstimate}
                  onChange={(e) => {
                    setTotalEstimate(e.target.value);
                    setTotalUserOverrode(e.target.value !== "");
                  }}
                  placeholder="Auto-filled from lines"
                  className="h-9 mt-1.5 text-base font-semibold"
                  data-testid="mqe-total"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Sum of qty&nbsp;×&nbsp;unit price across all lines.
                </p>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Overall Lead Time</Label>
                <Input
                  value={leadTime}
                  onChange={(e) => setLeadTime(e.target.value)}
                  placeholder="e.g. 5-7 business days"
                  className="h-9 mt-1.5"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Free-text. Best estimate across all lines on this quote.
                </p>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Freight Estimate&nbsp;($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={freightEstimate}
                  onChange={(e) => setFreightEstimate(e.target.value)}
                  placeholder={backorder.use_hospital_courier ? "Courier — n/a" : "0.00"}
                  className="h-9 mt-1.5"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  {backorder.use_hospital_courier
                    ? "Hospital courier in use — freight not applicable."
                    : "Shipping cost quoted by the supplier."}
                </p>
              </div>
            </div>
          </section>

          {/* Notes */}
          <section>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Notes</Label>
            <p className="text-[11px] text-slate-500 mt-0.5 mb-1.5">Anything we need to remember about this quote — caveats, MOQ, substitutions, partial fills, etc.</p>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Supplier can only fulfill 60% of qty; remainder back-ordered for 3 weeks. MOQ 5 cases." />
          </section>
        </div>

        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); submit(e); }}
            disabled={saving}
            className={`${isEdit ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"} gap-1.5`}
            data-testid="mqe-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Save changes" : "Save quote"}
          </Button>
        </div>
      </form>
    </div>
  );
}
